import clsx from "clsx";
import {
  FC,
  PropsWithChildren,
  Suspense,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  useFragment,
  useLazyLoadQuery,
  usePaginationFragment,
} from "react-relay";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { graphql } from "relay-runtime";
import {
  assetPairFilter,
  assetPairSearchRank,
  priceFormatter,
} from "rujira.js";
import {
  AssetLabel,
  compareBigInts,
  compareStrings,
  Decimal,
  Fiat,
  IconDenom,
  Icons,
  Input,
  nFormatter,
  SortDir,
  SortableTableHeader,
  TableFrame,
  TablePagination,
  useTablePagination,
  useTableSort,
  useTranslation,
} from "rujira.ui";

import { SortBar } from "../../common/components/SortBar";
import { StickyBar } from "../../common/components/StickyBar";
import { useIsMobile } from "../../hooks/useBreakpoint";
import { useListTopScroll } from "../../hooks/useListTopScroll";
import { useScrollEdges } from "../../hooks/useScrollEdges";
import { assetToTradeUrlSegment } from "../../services/assetUrl";
import { useFavorites } from "../../services/favorites";
import { isPeggedPair } from "../pairClassification";
import { removeMarketControlParams } from "../marketUrlState";
import { pairSwitchTradeUrlParams, searchFromParams } from "../tradeUrlState";
import {
  MarketsItemFragment$data,
  MarketsItemFragment$key,
} from "./__generated__/MarketsItemFragment.graphql";
import { PairRow } from "./mobile/PairRow";
import { PairRows, PairRowsFallback } from "./mobile/PairRows";
import {
  MarketsListFragment$data,
  MarketsListFragment$key,
} from "./__generated__/MarketsListFragment.graphql";
import { MarketsPaginationQuery } from "./__generated__/MarketsPaginationQuery.graphql";
import { MarketsQuery } from "./__generated__/MarketsQuery.graphql";

enum Section {
  Featured = "Featured",
  Usdc = "USDC",
  Btc = "BTC",
  Eth = "ETH",
  Pegged = "Pegged",
  All = "All",
  Favorites = "favorites",
}

const sections: Section[] = [
  Section.Featured,
  //Section.Usdc,
  Section.Btc,
  Section.Eth,
  Section.Pegged,
  Section.All,
  Section.Favorites,
];

const checkFeatured = (base: string): boolean =>
  [
    "AVAX-AVAX",
    "BASE-ETH",
    "BCH-BCH",
    "BSC-BNB",
    "BTC-BTC",
    "DOGE-DOGE",
    "ETH-ETH",
    "GAIA-ATOM",
    "LTC-LTC",
    "THOR.AUTO",
    "THOR.LQDY",
    "THOR.RUJI",
    "THOR.RUNE",
    "THOR.TCY",
    "XRP-XRP",
  ].includes(base);

const sectionFilter =
  (favorites: /* Record<string, boolean> */ Array<string>, section?: Section) =>
  (a: {
    id: string;
    address: string;
    assetBase: { asset: string; metadata: { symbol: string } };
    assetQuote: { asset: string; metadata: { symbol: string } };
  }): boolean => {
    if (!section) return true;
    if (section === Section.All) return true;
    if (section === Section.Favorites) return favorites.includes(a.id);
    if (section === Section.Featured) return checkFeatured(a.assetBase.asset);
    if (section === Section.Pegged)
      return isPeggedPair(
        a.assetBase.metadata.symbol,
        a.assetQuote.metadata.symbol
      );

    return (
      a.assetBase.metadata.symbol === section ||
      a.assetQuote.metadata.symbol === section
    );
  };

const q = graphql`
  query MarketsQuery {
    ...MarketsListFragment
  }
`;

const marketsFragment = graphql`
  fragment MarketsListFragment on RootQueryType
  @argumentDefinitions(
    count: { type: "Int", defaultValue: 100 }
    cursor: { type: "String" }
  )
  @refetchable(queryName: "MarketsPaginationQuery") {
    finV2(first: $count, after: $cursor, sortBy: VOLUME_USD, sortDir: DESC)
      @connection(key: "MarketsListFragment_finV2") {
      edges {
        node {
          id
          address
          assetBase {
            asset
            chain
            metadata {
              symbol
            }
          }
          assetQuote {
            asset
            chain
            metadata {
              symbol
            }
          }
          summary {
            last
            change
            volumeUsd
          }
          ...MarketsItemFragment
        }
      }
    }
  }
`;

const MARKET_SORT_KEYS = [
  "pair",
  "lastPrice",
  "change24h",
  "volume24h",
] as const;
type MarketSortKey = (typeof MARKET_SORT_KEYS)[number];

const MARKET_SORTS: readonly { key: MarketSortKey; label: string }[] = [
  { key: "pair", label: "pair" },
  { key: "lastPrice", label: "lastPrice" },
  { key: "change24h", label: "change24h" },
  { key: "volume24h", label: "volume24h" },
];

const MARKETS_PAGE_COLUMNS = [
  { id: "logo", width: "4rem" },
  { id: "pair", width: "22%", minWidth: "12rem" },
  { id: "last-price", width: "26%", minWidth: "12rem" },
  { id: "change", width: "20%", minWidth: "8rem" },
  { id: "volume", width: "32%", minWidth: "13rem" },
] as const;

const MARKETS_PANEL_COLUMNS = [
  { id: "logo", width: "3rem" },
  { id: "pair", width: "6rem" },
  { id: "last-price", width: "8rem" },
  { id: "change", width: "6rem" },
  { id: "volume", width: "8rem" },
] as const;

const marketDefaultSortDir = (sortBy: MarketSortKey): SortDir =>
  sortBy === "pair" ? SortDir.Asc : SortDir.Desc;

const MarketsTable: FC<
  PropsWithChildren<{
    isMarketsPage: boolean;
    sortBy: MarketSortKey;
    sortDir: SortDir;
    toggleSort: (value: MarketSortKey) => void;
  }>
> = ({ children, isMarketsPage, sortBy, sortDir, toggleSort }) => {
  const { t } = useTranslation();
  const columns = isMarketsPage ? MARKETS_PAGE_COLUMNS : MARKETS_PANEL_COLUMNS;
  const sortHeaderProps = { active: sortBy, dir: sortDir, onSort: toggleSort };

  return (
    <TableFrame
      className={!isMarketsPage ? "trade__markets-overflow mt-3" : undefined}
      columns={columns}
      stickyFirstColumn
      tableClassName={clsx({
        "table--big condensed": isMarketsPage,
        "table--va-m": !isMarketsPage,
      })}>
      <thead className={!isMarketsPage ? "border" : undefined}>
        <tr>
          <th aria-hidden="true" />
          <SortableTableHeader
            {...sortHeaderProps}
            label={t("pair")}
            value="pair"
          />
          <SortableTableHeader
            {...sortHeaderProps}
            label={t("lastPrice")}
            value="lastPrice"
            align="right"
          />
          <SortableTableHeader
            {...sortHeaderProps}
            label={t("change24h")}
            value="change24h"
            align="right"
          />
          <SortableTableHeader
            {...sortHeaderProps}
            label={t("volume24h")}
            value="volume24h"
            align="right"
          />
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </TableFrame>
  );
};

const SectionTabs: FC<{
  section: Section;
  onChange: (section: Section) => void;
  details?: boolean;
  full?: boolean;
  className?: string;
}> = ({ section, onChange, details, full, className }) => {
  const { t } = useTranslation();
  const tabs = useScrollEdges<HTMLElement>();

  return (
    <div
      className={clsx("trade__markets-tabs", className, {
        "trade__markets-tabs--start": tabs.edges.start,
        "trade__markets-tabs--end": tabs.edges.end,
      })}>
      <nav
        ref={tabs.ref}
        className={clsx("tabs tabs--sm gap-1", { "tabs--full": full })}>
        {sections.map((s) => (
          <a
            key={s}
            className={clsx({
              selected: section === s,
              "px-1 lonely": s === Section.Favorites,
            })}
            onClick={() => onChange(s)}>
            {s === Section.Favorites ? (
              <Icons.StarSolid className="w-2 h-2 ml-0" />
            ) : s === Section.Pegged ? (
              t("pegged")
            ) : (
              s
            )}
          </a>
        ))}
        {details && (
          <Link to="../.." relative="path" className={"transparent"}>
            {t("details")}
            <Icons.External className="w-2 h-2" />
          </Link>
        )}
      </nav>
    </div>
  );
};

export const Markets = ({ closeDrawer }: { closeDrawer?: () => void }) => (
  <MarketsInner closeDrawer={closeDrawer} />
);

const MarketsInner = ({ closeDrawer }: { closeDrawer?: () => void }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [section, setSection] = useState<Section>(Section.Featured);
  const [query, setQuery] = useState("");
  const [searched, setSearched] = useState(false);
  const { sortBy, sortDir, toggleSort } = useTableSort<MarketSortKey>({
    initialBy: "volume24h",
    initialDir: SortDir.Desc,
    defaultDir: marketDefaultSortDir,
  });

  useEffect(() => {
    if (!searched && section === Section.Featured && query !== "") {
      setSection(Section.All);
      setSearched(true);
    }
  }, [section, query]);

  useEffect(() => {
    const params = removeMarketControlParams(location.search);
    if (!params) return;
    navigate(
      {
        pathname: location.pathname,
        search: searchFromParams(params),
        hash: location.hash,
      },
      { replace: true }
    );
  }, [location.hash, location.pathname, location.search, navigate]);

  const { base: selectedBase, quote: selectedQuote } = useParams<{
    base: string;
    quote: string;
  }>();
  const isMarketsPage = !selectedBase || !selectedQuote;

  const rows = useIsMobile();
  const stickyFilters = rows && isMarketsPage;
  const { anchor } = useListTopScroll(stickyFilters, [
    sortBy,
    sortDir,
    section,
    query,
  ]);
  const sorter = (
    <SortBar
      label={t("sort")}
      options={MARKET_SORTS.map(({ key, label }) => ({ key, label: t(label) }))}
      sortKey={sortBy}
      sortDir={sortDir}
      onKeyChange={toggleSort}
      onDirToggle={() => toggleSort(sortBy)}
    />
  );

  const touch = matchMedia("(hover: none)").matches;
  const marketsTable = (
    <Suspense
      fallback={
        <MarketsTable
          isMarketsPage={isMarketsPage}
          sortBy={sortBy}
          sortDir={sortDir}
          toggleSort={toggleSort}>
          <Fallback />
        </MarketsTable>
      }>
      <Content
        closeDrawer={closeDrawer}
        query={query}
        section={section}
        sortBy={sortBy}
        sortDir={sortDir}
        toggleSort={toggleSort}
        isMarketsPage={isMarketsPage}
      />
    </Suspense>
  );

  return (
    <>
      <div className={!isMarketsPage ? "pr-2" : ""}>
        <Input
          autoFocus={!touch}
          containerClassName="trade__markets-search"
          label={t("search")}
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}>
          {query !== "" && (
            <Icons.MinusCircle
              className="input-container__clear"
              onClick={() => {
                setQuery("");
                setSearched(false);
              }}
            />
          )}
        </Input>
        {!stickyFilters && (
          <div className={"flex dir-r"}>
            <SectionTabs
              className="mt-2 mt-md-1"
              section={section}
              onChange={setSection}
              details={!isMarketsPage}
            />
          </div>
        )}
        {rows && !isMarketsPage && <div className="mt-1">{sorter}</div>}
      </div>
      {stickyFilters ? (
        <>
          <div ref={anchor} aria-hidden className="scroll-anchor" />
          <StickyBar
            collapseLabel={t("showFilterSort")}
            className="sticky-bar--clear sticky-bar--flush pt-1 pb-2">
            <div className="flex dir-c gap-1 w-full">
              <SectionTabs full section={section} onChange={setSection} />
              {sorter}
            </div>
          </StickyBar>
          <Suspense fallback={<PairRowsFallback />}>
            <Content
              rows
              query={query}
              section={section}
              sortBy={sortBy}
              sortDir={sortDir}
              toggleSort={toggleSort}
              isMarketsPage={isMarketsPage}
            />
          </Suspense>
        </>
      ) : rows ? (
        <div className="trade__markets-overflow mt-2">
          <Suspense fallback={<PairRowsFallback />}>
            <Content
              rows
              closeDrawer={closeDrawer}
              query={query}
              section={section}
              sortBy={sortBy}
              sortDir={sortDir}
              toggleSort={toggleSort}
              isMarketsPage={isMarketsPage}
            />
          </Suspense>
        </div>
      ) : isMarketsPage ? (
        <div className="relative card p-3 mt-2">{marketsTable}</div>
      ) : (
        marketsTable
      )}
    </>
  );
};

type Node = NonNullable<
  NonNullable<
    NonNullable<MarketsListFragment$data["finV2"]["edges"]>[0]
  >["node"]
>;

const Content: FC<{
  closeDrawer?: () => void;
  query?: string;
  section?: Section;
  rows?: boolean;
  sortBy: MarketSortKey;
  sortDir: SortDir;
  toggleSort: (value: MarketSortKey) => void;
  isMarketsPage: boolean;
}> = ({
  closeDrawer,
  query,
  section,
  rows,
  sortBy,
  sortDir,
  toggleSort,
  isMarketsPage,
}) => {
  const { t } = useTranslation();
  const queryData = useLazyLoadQuery<MarketsQuery>(
    q,
    {},
    {
      fetchPolicy: "store-and-network",
    }
  );
  const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment<
    MarketsPaginationQuery,
    MarketsListFragment$key
  >(marketsFragment, queryData);
  const [loadError, setLoadError] = useState<Error | null>(null);

  // All sort keys and section/search filters need the complete market set.
  // Fetch in bounded pages, then reuse the Relay connection locally.
  useEffect(() => {
    if (!hasNext || isLoadingNext || loadError) return;
    loadNext(100, { onComplete: (error) => setLoadError(error ?? null) });
  }, [hasNext, isLoadingNext, loadNext, loadError]);

  const { favorites, addFavorite, removeFavorite } = useFavorites();

  const toggleFavorite = (v: string) => {
    if (favorites.includes(v)) {
      removeFavorite(v);
    } else {
      addFavorite(v);
    }
  };

  const items = useMemo(() => {
    const pairs: Node[] =
      data.finV2.edges?.flatMap((edge) => (edge?.node ? [edge.node] : [])) ??
      [];
    const filtered = pairs
      .filter(assetPairFilter(query))
      .filter(sectionFilter(favorites, section));
    const rank =
      query?.trim().toLowerCase() === "eth" ? assetPairSearchRank(query) : null;

    return [...filtered].sort((a, b) => {
      if (rank && sortBy === "pair") {
        const rankComparison = rank(a) - rank(b);
        if (rankComparison) return rankComparison;
      }

      switch (sortBy) {
        case "pair":
          return compareStrings(
            `${a.assetBase.metadata.symbol}/${a.assetQuote.metadata.symbol}`,
            `${b.assetBase.metadata.symbol}/${b.assetQuote.metadata.symbol}`,
            sortDir
          );
        case "lastPrice":
          return compareBigInts(
            BigInt(a.summary?.last || 0),
            BigInt(b.summary?.last || 0),
            sortDir
          );
        case "change24h":
          return compareBigInts(
            BigInt(a.summary?.change || 0),
            BigInt(b.summary?.change || 0),
            sortDir
          );
        case "volume24h":
          return compareBigInts(
            BigInt(a.summary?.volumeUsd || 0),
            BigInt(b.summary?.volumeUsd || 0),
            sortDir
          );
      }
    });
  }, [data.finV2.edges, favorites, query, section, sortBy, sortDir]);
  const { pageRows, pagination } = useTablePagination(items, {
    resetKey: `${query ?? ""}:${section ?? ""}:${sortBy}:${sortDir}:${favorites.join(",")}`,
  });

  const incomplete = hasNext || isLoadingNext || !!loadError;

  if (rows) {
    if (!items.length && loadError)
      return (
        <div className="text-center mt-3">
          <button
            type="button"
            className="button button--small"
            onClick={() => setLoadError(null)}>
            {t("common:retry")}
          </button>
        </div>
      );
    if (!items.length && incomplete) return <PairRowsFallback />;
    if (!items.length)
      return (
        <p className="text-center color-grey mt-3">{t("noResultsFound")}</p>
      );

    return (
      <>
        {incomplete && (
          <div className="text-center color-grey my-1" role="status">
            {loadError ? t("marketsLoadIncomplete") : t("marketsLoadingMore")}
            {loadError && (
              <button
                type="button"
                className="button button--small ml-1"
                onClick={() => setLoadError(null)}>
                {t("common:retry")}
              </button>
            )}
          </div>
        )}
        <PairRows>
          {items.map((x) => (
            <RowItem
              key={x.address}
              k={x}
              closeDrawer={closeDrawer}
              favorite={favorites.includes(x.id)}
              toggleFavorite={() => toggleFavorite(x.id)}
            />
          ))}
        </PairRows>
      </>
    );
  }

  const content = pageRows.length ? (
    pageRows.map((x) => (
      <Item
        key={x.address}
        k={x}
        closeDrawer={closeDrawer}
        favorite={favorites.includes(x.id)}
        toggleFavorite={() => toggleFavorite(x.id)}
      />
    ))
  ) : loadError ? null : incomplete ? (
    <Fallback />
  ) : (
    <tr>
      <td colSpan={5} className="text-center color-grey">
        <br />
        {t("noResultsFound")}
      </td>
    </tr>
  );

  return (
    <>
      <MarketsTable
        isMarketsPage={isMarketsPage}
        sortBy={sortBy}
        sortDir={sortDir}
        toggleSort={toggleSort}>
        {incomplete && (
          <tr>
            <td colSpan={5} className="text-center color-grey" role="status">
              {loadError ? t("marketsLoadIncomplete") : t("marketsLoadingMore")}
              {loadError && (
                <button
                  type="button"
                  className="button button--small ml-1"
                  onClick={() => setLoadError(null)}>
                  {t("common:retry")}
                </button>
              )}
            </td>
          </tr>
        )}
        {content}
      </MarketsTable>
      <TablePagination {...pagination} />
    </>
  );
};

// Relative link to a pair. Resolved against the path, so from a pair page it
// climbs out of the current /base/quote first; the locale prefix is kept.
const usePairUrl = (x: MarketsItemFragment$data) => {
  const location = useLocation();
  const { base: selectedBase, quote: selectedQuote } = useParams<{
    base: string;
    quote: string;
  }>();
  const nextParams = pairSwitchTradeUrlParams(
    new URLSearchParams(location.search)
  );
  const next = `${assetToTradeUrlSegment(x.assetBase)}/${assetToTradeUrlSegment(x.assetQuote)}${nextParams ? searchFromParams(nextParams) : ""}`;
  return selectedBase && selectedQuote ? `../../${next}` : next;
};

const RowItem: FC<{
  k: MarketsItemFragment$key;
  closeDrawer?: () => void;
  favorite: boolean;
  toggleFavorite: () => void;
}> = ({ k, closeDrawer, favorite, toggleFavorite }) => {
  const x = useFragment(fragment, k);
  const url = usePairUrl(x);

  return (
    <PairRow
      pair={x}
      url={url}
      favorite={favorite}
      onToggleFavorite={toggleFavorite}
      onNavigate={closeDrawer}
    />
  );
};

const fragment = graphql`
  fragment MarketsItemFragment on FinPair {
    id
    address
    assetBase {
      asset
      chain
      type
      metadata {
        symbol
        decimals
      }
    }
    assetQuote {
      asset
      chain
      type
      metadata {
        symbol
        decimals
      }
      price {
        current
      }
    }
    summary {
      last
      change
      volumeUsd
      volume {
        amount
        asset {
          metadata {
            symbol
          }
        }
      }
    }
  }
`;

const Item: FC<{
  k: MarketsItemFragment$key;
  closeDrawer?: () => void;
  toggleFavorite: () => void;
  favorite: boolean;
}> = ({ k, closeDrawer, toggleFavorite, favorite }) => {
  const navigate = useNavigate();
  const { base: selectedBase, quote: selectedQuote } = useParams<{
    base: string;
    quote: string;
  }>();
  const x = useFragment(fragment, k);
  const url = usePairUrl(x);
  const change = BigInt(x.summary?.change || 0);
  const isMarketsPage = !selectedBase || !selectedQuote;

  return (
    <tr
      key={x.address}
      className="pointer"
      onClick={() => {
        navigate(url, {
          relative: "path",
          state: { pairId: x.id },
        });
        closeDrawer?.();
      }}>
      <td className="nopad">
        <button
          className="transparent py-1 pr-0.5 color-grey pointer"
          onClick={(e) => {
            e.stopPropagation();
            toggleFavorite();
          }}>
          {favorite ? (
            <Icons.StarSolid
              className={isMarketsPage ? "w-3 h-3" : "w-2 h-2"}
            />
          ) : (
            <Icons.Star className={isMarketsPage ? "w-3 h-3" : "w-2 h-2"} />
          )}
        </button>
      </td>
      <td>
        <div
          className={clsx({
            "flex ai-c": true,
            "fs-20 fs-lg-26 fw-400": isMarketsPage,
          })}>
          <div className={isMarketsPage ? "lp mt-0.5" : "pair mr-0.5"}>
            <IconDenom
              className={isMarketsPage ? "" : "w-2"}
              denom={x.assetBase.metadata.symbol}
            />
            <IconDenom
              className={isMarketsPage ? "" : "w-2"}
              denom={x.assetQuote.metadata.symbol}
            />
          </div>
          <div
            className={clsx({
              "flex ai-b": true,
              "ml-1": isMarketsPage,
            })}>
            <AssetLabel
              asset={x.assetBase}
              Container={({ children }) => (
                <span
                  className={clsx({
                    "color-grey": true,
                    "fs-11 fw-500": !isMarketsPage,
                    "fs-14 fs-lg-16": isMarketsPage,
                  })}>
                  {children}
                </span>
              )}
            />

            <span className="color-grey ml-0.5">
              <AssetLabel
                asset={x.assetQuote}
                Container={({ children }) => (
                  <span
                    className={clsx({
                      "color-grey": true,
                      "fs-11 fw-500": !isMarketsPage,
                      "fs-14 fs-lg-16": isMarketsPage,
                    })}>
                    {children}
                  </span>
                )}
              />
            </span>
          </div>
        </div>
      </td>
      <td className="text-right">
        <span
          className={clsx({
            "condensed fs-16 fs-lg-18": isMarketsPage,
          })}>
          {priceFormatter(BigInt(x?.summary?.last || 0))}
          <span
            className={clsx("color-grey", {
              "fs-13 fs-lg-15": isMarketsPage,
              "fs-12": !isMarketsPage,
            })}>
            {" " + x.assetQuote.metadata.symbol}
          </span>
        </span>
        {isMarketsPage && (
          <>
            <br />
            <Fiat
              amount={
                BigInt(x?.summary?.last || 0) *
                BigInt(x.assetQuote.price?.current || 1)
              }
              decimals={24}
              symbol="$"
              className="color-grey fs-14 condensed"
            />
          </>
        )}
      </td>
      <td
        className={clsx({
          "text-right": true,
          "color-teal": change > 0n,
          "color-red": change < 0n,
          "color-grey": change === 0n,
        })}>
        {change > 0n ? "+" : ""}
        {(Number(change) / 10 ** 10).toLocaleDecimal(2)}%
      </td>
      <td
        className={clsx({
          "text-right": true,
          "fs-14 fs-lg-15": isMarketsPage,
        })}>
        ${nFormatter(BigInt(x.summary?.volumeUsd || 0), 2, 8)}
        {isMarketsPage && (
          <>
            <br />
            <Decimal
              amount={BigInt(x.summary?.volume.amount || 0)}
              decimals={8}
              symbol={x.summary?.volume.asset.metadata.symbol}
              className="color-grey fs-14 condensed"
            />
          </>
        )}
      </td>
    </tr>
  );
};

const Fallback = () => (
  <>
    {[...Array(10)].map((_, i) => (
      <tr key={`skeleton_${i}`}>
        <td className="nopad w-1"></td>
        <td className={`opacity-${20 - i * 2}`}>
          <div className="flex ai-c">
            <div className="pair mr-0.5">
              <div className="icon-denom skeleton h-2 w-2 br-2" />
              <div className="icon-denom skeleton h-2 w-2 br-2" />
            </div>
            <div className="w-full skeleton h-1.5 w-1.5 br-2" />
          </div>
        </td>
        <td className={`opacity-${20 - i * 2}`}>
          <div className="w-full skeleton h-1.5 w-1.5 br-2" />
        </td>
        <td className={`opacity-${20 - i * 2}`}>
          <div className="w-full skeleton h-1.5 w-1.5 br-2" />
        </td>
        <td className={`opacity-${20 - i * 2}`}>
          <div className="w-full skeleton h-1.5 w-1.5 br-2" />
        </td>
      </tr>
    ))}
  </>
);
